// Usage tracking service for monitoring feature usage and enforcing limits

import { subscriptionService } from "./subscriptionService";
import { SubscriptionTier } from "../constants/subscriptionLimits";
import { storageService } from "./storageService";
import { waitForTransaction } from "./indexedDbTx";

export type UsageEventType = "ai_search" | "ai_analysis" | "export";

interface UsageMetrics {
  userId: string;
  periodStart: string; // Start of current billing period
  periodEnd: string; // End of current billing period
  aiSearches: {
    used: number;
    limit: number;
  };
  aiAnalyses: {
    used: number;
    limit: number;
  };
  exports: {
    used: number;
    limit: number;
  };
  lastUpdated: string;
}

interface UsageEvent {
  id: string;
  userId: string;
  eventType: UsageEventType;
  timestamp: string;
  metadata?: Record<string, any>;
}

const STORE_USAGE = "usage";
const STORE_USAGE_EVENTS = "usageEvents";

async function openDB(): Promise<IDBDatabase> {
  return storageService.openDB();
}

function getCurrentPeriod(): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(
    now.getFullYear(),
    now.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

export const usageService = {
  /**
   * Get current usage metrics
   */
  async getUsage(userId: string): Promise<UsageMetrics> {
    try {
      const db = await openDB();
      const subscription = await subscriptionService.getSubscription(userId);
      const limits = subscriptionService.getTierLimits(subscription.tier);
      const period = getCurrentPeriod();

      return new Promise((resolve) => {
        const tx = db.transaction(STORE_USAGE, "readonly");
        const request = tx.objectStore(STORE_USAGE).get(userId);

        request.onsuccess = () => {
          const existing = request.result as UsageMetrics | undefined;

          if (existing && existing.periodStart === period.start) {
            // Same period, return existing
            resolve(existing);
          } else {
            // New period or no existing data, create new
            const newUsage: UsageMetrics = {
              userId,
              periodStart: period.start,
              periodEnd: period.end,
              aiSearches: {
                used: 0,
                limit: limits.aiSearches === -1 ? -1 : limits.aiSearches,
              },
              aiAnalyses: {
                used: 0,
                limit: limits.aiAnalyses === -1 ? -1 : limits.aiAnalyses,
              },
              exports: {
                used: 0,
                limit: limits.exports === -1 ? -1 : limits.exports,
              },
              lastUpdated: new Date().toISOString(),
            };

            // Save new usage
            this.saveUsage(newUsage).catch(() => {});
            resolve(newUsage);
          }
        };

        request.onerror = () => {
          // Create default usage on error
          const defaultUsage: UsageMetrics = {
            userId,
            periodStart: period.start,
            periodEnd: period.end,
            aiSearches: {
              used: 0,
              limit: limits.aiSearches === -1 ? -1 : limits.aiSearches,
            },
            aiAnalyses: {
              used: 0,
              limit: limits.aiAnalyses === -1 ? -1 : limits.aiAnalyses,
            },
            exports: {
              used: 0,
              limit: limits.exports === -1 ? -1 : limits.exports,
            },
            lastUpdated: new Date().toISOString(),
          };
          resolve(defaultUsage);
        };
      });
    } catch (error) {
      console.warn("Failed to get usage:", error);
      // Return default usage
      const subscription = await subscriptionService.getSubscription(userId);
      const limits = subscriptionService.getTierLimits(subscription.tier);
      const period = getCurrentPeriod();
      return {
        userId,
        periodStart: period.start,
        periodEnd: period.end,
        aiSearches: {
          used: 0,
          limit: limits.aiSearches === -1 ? -1 : limits.aiSearches,
        },
        aiAnalyses: {
          used: 0,
          limit: limits.aiAnalyses === -1 ? -1 : limits.aiAnalyses,
        },
        exports: {
          used: 0,
          limit: limits.exports === -1 ? -1 : limits.exports,
        },
        lastUpdated: new Date().toISOString(),
      };
    }
  },

  /**
   * Save usage metrics
   */
  async saveUsage(usage: UsageMetrics): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_USAGE, "readwrite");
      const store = tx.objectStore(STORE_USAGE);

      await new Promise<void>((resolve, reject) => {
        const request = store.put(usage);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await waitForTransaction(tx);
    } catch (error) {
      console.warn("Failed to save usage:", error);
    }
  },

  /**
   * Track usage event
   */
  async trackUsage(
    userId: string,
    eventType: UsageEventType,
  ): Promise<boolean> {
    try {
      const usage = await this.getUsage(userId);

      // Check if limit is reached
      let limit: number;
      let used: number;

      switch (eventType) {
        case "ai_search":
          limit = usage.aiSearches.limit;
          used = usage.aiSearches.used;
          break;
        case "ai_analysis":
          limit = usage.aiAnalyses.limit;
          used = usage.aiAnalyses.used;
          break;
        case "export":
          limit = usage.exports.limit;
          used = usage.exports.used;
          break;
        default:
          return false;
      }

      // Check if within limit (unlimited = -1)
      if (limit !== -1 && used >= limit) {
        return false; // Limit reached
      }

      // Increment usage
      switch (eventType) {
        case "ai_search":
          usage.aiSearches.used++;
          break;
        case "ai_analysis":
          usage.aiAnalyses.used++;
          break;
        case "export":
          usage.exports.used++;
          break;
      }

      usage.lastUpdated = new Date().toISOString();
      await this.saveUsage(usage);

      // Log event
      await this.logEvent(userId, eventType);

      return true;
    } catch (error) {
      console.warn("Failed to track usage:", error);
      return false;
    }
  },

  /**
   * Check if user can perform action
   */
  async canPerformAction(
    userId: string,
    action: UsageEventType,
  ): Promise<{ allowed: boolean; reason?: string }> {
    const subscription = await subscriptionService.getSubscription(userId);
    const limits = subscriptionService.getTierLimits(subscription.tier);
    const usage = await this.getUsage(userId);

    let limit: number;
    let used: number;

    switch (action) {
      case "ai_search":
        limit = limits.aiSearches;
        used = usage.aiSearches.used;
        break;
      case "ai_analysis":
        limit = limits.aiAnalyses;
        used = usage.aiAnalyses.used;
        break;
      case "export":
        limit = limits.exports;
        used = usage.exports.used;
        break;
      default:
        return { allowed: false, reason: "Unknown action" };
    }

    // Check if feature is available for tier
    if (limit === 0) {
      return {
        allowed: false,
        reason: `This feature is not available in the ${subscription.tier} tier. Upgrade to Pro or Business to access this feature.`,
      };
    }

    // Check if limit is reached
    if (limit !== -1 && used >= limit) {
      return {
        allowed: false,
        reason: `You've reached your monthly limit of ${limit} ${action.replace("_", " ")}. Upgrade to get unlimited access.`,
      };
    }

    return { allowed: true };
  },

  /**
   * Log usage event
   */
  async logEvent(
    userId: string,
    eventType: UsageEventType,
    metadata?: Record<string, any>,
  ): Promise<void> {
    try {
      const db = await openDB();
      const tx = db.transaction(STORE_USAGE_EVENTS, "readwrite");
      const store = tx.objectStore(STORE_USAGE_EVENTS);

      const event: UsageEvent = {
        id: `${userId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userId,
        eventType,
        timestamp: new Date().toISOString(),
        metadata,
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(event);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      await waitForTransaction(tx);
    } catch (error) {
      console.warn("Failed to log event:", error);
    }
  },

  /**
   * Get usage statistics
   */
  async getUsageStats(userId: string): Promise<{
    aiSearches: { used: number; limit: number; percentage: number };
    aiAnalyses: { used: number; limit: number; percentage: number };
    exports: { used: number; limit: number; percentage: number };
  }> {
    const usage = await this.getUsage(userId);

    const calculatePercentage = (used: number, limit: number): number => {
      if (limit === -1) return 0; // Unlimited
      if (limit === 0) return 0; // Not available
      return Math.min(100, Math.round((used / limit) * 100));
    };

    return {
      aiSearches: {
        used: usage.aiSearches.used,
        limit: usage.aiSearches.limit,
        percentage: calculatePercentage(
          usage.aiSearches.used,
          usage.aiSearches.limit,
        ),
      },
      aiAnalyses: {
        used: usage.aiAnalyses.used,
        limit: usage.aiAnalyses.limit,
        percentage: calculatePercentage(
          usage.aiAnalyses.used,
          usage.aiAnalyses.limit,
        ),
      },
      exports: {
        used: usage.exports.used,
        limit: usage.exports.limit,
        percentage: calculatePercentage(
          usage.exports.used,
          usage.exports.limit,
        ),
      },
    };
  },

  /**
   * Reset usage for new period (called automatically when period changes)
   */
  async resetUsagePeriod(userId: string): Promise<void> {
    const subscription = await subscriptionService.getSubscription(userId);
    const limits = subscriptionService.getTierLimits(subscription.tier);
    const period = getCurrentPeriod();

    const newUsage: UsageMetrics = {
      userId,
      periodStart: period.start,
      periodEnd: period.end,
      aiSearches: {
        used: 0,
        limit: limits.aiSearches === -1 ? -1 : limits.aiSearches,
      },
      aiAnalyses: {
        used: 0,
        limit: limits.aiAnalyses === -1 ? -1 : limits.aiAnalyses,
      },
      exports: {
        used: 0,
        limit: limits.exports === -1 ? -1 : limits.exports,
      },
      lastUpdated: new Date().toISOString(),
    };

    await this.saveUsage(newUsage);
  },
};
